/**
 * GlobalHome — registry-driven platform overview (Portal v2 P2, DEC-063).
 *
 * KPI count strip (per-module entity totals from the CRUD list endpoints) +
 * a recent-activity table (first timestamped entity of the first module) +
 * the module card grid. Zero-entity platforms degrade to the card grid alone.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MODULE_LIST, type EntityRef, type ModuleConfig } from '../registry/portalRegistry';
import { PORTAL_META } from '../registry/portalMeta';
import { authFetch } from '../auth/authClient';

const KPI_PER_MODULE = 3;
const RECENT_ROWS = 5;
const SYSTEM_COLUMNS = new Set(['id', 'tenant_id', 'created_at', 'updated_at', 'created_by', 'updated_by']);

async function fetchJson(url: string): Promise<unknown> {
  // authFetch: session-aware transport (refresh-on-401, expired-session event)
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

interface KpiTarget {
  module: ModuleConfig;
  entity: EntityRef;
}

function kpiTargets(): KpiTarget[] {
  return MODULE_LIST.flatMap((module) =>
    module.entities.slice(0, KPI_PER_MODULE).map((entity) => ({ module, entity })),
  );
}

function extractTotal(data: unknown): number | null {
  if (Array.isArray(data)) return data.length;
  if (data && typeof data === 'object' && 'total' in data) {
    const t = (data as Record<string, unknown>).total;
    return typeof t === 'number' ? t : null;
  }
  return null;
}

function extractItems(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).items)) {
    return (data as { items: Record<string, unknown>[] }).items;
  }
  return [];
}

const KpiStrip: React.FC = () => {
  const navigate = useNavigate();
  const targets = kpiTargets();
  const results = useQueries({
    queries: targets.map(({ entity }) => ({
      queryKey: ['overview-kpi', entity.endpoint],
      // 200 not 1: legacy bare-array list endpoints have no total envelope —
      // extractTotal falls back to counting rows (exact up to 200)
      queryFn: () => fetchJson(`/api/v1/${entity.endpoint}?page_size=200`),
      staleTime: 60_000,
      retry: 1,
    })),
  });

  if (!targets.length) return null;

  return (
    <Grid container spacing={1.5} sx={{ mb: 3 }}>
      {targets.map(({ module, entity }, i) => {
        const q = results[i];
        const total = q?.data !== undefined ? extractTotal(q.data) : null;
        return (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={`${module.id}-${entity.name}`}>
            <Card variant="outlined" sx={{ borderTop: `2px solid ${module.accent}` }}>
              <CardActionArea onClick={() => navigate(`/${module.id}/admin/${entity.endpoint}`)}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {entity.label}
                  </Typography>
                  {q?.isLoading ? (
                    <Skeleton width={48} height={36} />
                  ) : q?.isError ? (
                    <Typography variant="h6" color="text.disabled">—</Typography>
                  ) : (
                    <Typography variant="h5" fontWeight={700}>
                      {total ?? '—'}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

const RecentActivity: React.FC = () => {
  // First timestamped entity across modules, in registry order.
  const target = MODULE_LIST.flatMap((m) => m.entities)
    .find((e) => e.hasTimestamps);
  const query = useQuery({
    queryKey: ['overview-recent', target?.endpoint],
    queryFn: () => fetchJson(`/api/v1/${target!.endpoint}?page_size=${RECENT_ROWS}`),
    enabled: !!target,
    staleTime: 60_000,
    retry: 1,
  });

  if (!target) return null;
  if (query.isError) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Recent activity unavailable — check that the {target.label} service is reachable.
      </Alert>
    );
  }

  const rows = extractItems(query.data);
  const columns = rows.length
    ? Object.keys(rows[0]).filter((k) => !SYSTEM_COLUMNS.has(k)).slice(0, 4)
    : [];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Recent {target.label} activity
      </Typography>
      <Card variant="outlined">
        {query.isLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No {target.label} records yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c} sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      {c.replace(/_/g, ' ')}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={String(row.id ?? i)} hover>
                    {columns.map((c) => (
                      <TableCell key={c}>{String(row[c] ?? '')}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
};

const GlobalHome: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        {PORTAL_META.platformTitle}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Platform overview
      </Typography>

      <KpiStrip />
      <RecentActivity />

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Modules
      </Typography>
      <Grid container spacing={2}>
        {MODULE_LIST.map((mod) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={mod.id}>
            <Card variant="outlined" sx={{ '&:hover': { borderColor: mod.accent } }}>
              <CardActionArea onClick={() => navigate(`/${mod.id}`)} sx={{ p: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ color: mod.accent, mb: 0.5 }}>
                    {mod.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {mod.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default GlobalHome;
